import { HttpError, httpRequest } from '@app/httpRequest.js';
import type { RootState } from '@app/store/store.js';
import type { CancelTriggers } from '@shared/cancelRegister.js';
import type { OsmFeatureId } from '@shared/types/featureId.js';
import { type OsmResult, OsmResultSchema } from './types.js';

function osmApiUrl({ elementType, id }: OsmFeatureId): string {
  const base = process.env['OSM_API_URL'];

  return elementType === 'node'
    ? `${base}/api/0.6/node/${id}.json`
    : `${base}/api/0.6/${elementType}/${id}/full.json`;
}

/**
 * Fetches OSM elements with their dependencies (member nodes/ways) and returns
 * the parsed `elements` array of them all. The OSM API has no batch endpoint,
 * so a batch is a request per element, run together and concatenated.
 *
 * An element that doesn't exist is simply left out of the result — telling
 * which of a batch came back is `assembleOsmGeojson`'s job, since only the
 * caller knows what a missing one means for it.
 */
export async function fetchOsmElements(
  ids: readonly OsmFeatureId[],
  options: { getState: () => RootState } & CancelTriggers,
): Promise<OsmResult> {
  if (ids.length === 0) {
    return { elements: [] };
  }

  const results = await Promise.allSettled(
    ids.map(async (id) =>
      OsmResultSchema.parse(
        await (
          await httpRequest({
            ...options,
            url: osmApiUrl(id),
            expectedStatus: 200,
          })
        ).json(),
      ),
    ),
  );

  // 404 and 410 are how the OSM API says an element never existed or is
  // deleted, which is a missing element rather than a failed fetch — anything
  // else is one, and fails the whole batch. Otherwise one request going wrong
  // (or being aborted) would read as its element having been deleted, and take
  // the pin off the map.
  for (const result of results) {
    if (
      result.status === 'rejected' &&
      !(
        result.reason instanceof HttpError &&
        (result.reason.status === 404 || result.reason.status === 410)
      )
    ) {
      throw result.reason;
    }
  }

  return {
    elements: results.flatMap((result) =>
      result.status === 'fulfilled' ? result.value.elements : [],
    ),
  };
}
